/**
 * Entra External ID (CIAM) constants for the server-side (BFF) auth flow.
 *
 * These are NON-SECRET identifiers (tenant id, client id, endpoints) — the
 * same values the backend serves from appsettings via GET /api/auth/config.
 * They are compiled into both the server and client bundles; the CLIENT SECRET
 * is never referenced here (see src/auth.ts, read from env at runtime).
 *
 * Server-side env overrides (AUTH_ENTRA_TENANT_ID / AUTH_ENTRA_CLIENT_ID) exist
 * for the unlikely event the tenant or app registration moves — the browser
 * bundle always uses the defaults below, so keep them current.
 */

const DEFAULT_TENANT_ID = "18c87119-59c6-4683-8d0a-7ca9d41f5c37"
const DEFAULT_CLIENT_ID = "d41a19f0-7596-4962-8b1d-c08273d787af"
const DEFAULT_TENANT_SUBDOMAIN = "agenticplan"

export const AUTH_TENANT_ID =
  process.env.AUTH_ENTRA_TENANT_ID ?? DEFAULT_TENANT_ID
export const AUTH_CLIENT_ID =
  process.env.AUTH_ENTRA_CLIENT_ID ?? DEFAULT_CLIENT_ID
export const AUTH_TENANT_SUBDOMAIN =
  process.env.AUTH_ENTRA_TENANT_SUBDOMAIN ?? DEFAULT_TENANT_SUBDOMAIN

/**
 * CIAM authority in the TENANT-ID host form. The friendly host
 * (agenticplan.ciamlogin.com) serves the same endpoints, but the OIDC
 * discovery document and the id_token `iss` claim both use this canonical
 * form — and Auth.js/oauth4webapi strictly require the configured issuer to
 * match both (discovery mismatch fails sign-in with `error=Configuration`;
 * iss mismatch fails the code exchange). Verified against
 * `/.well-known/openid-configuration` on 2026-07-17.
 */
export const AUTH_AUTHORITY = `https://${AUTH_TENANT_ID}.ciamlogin.com/${AUTH_TENANT_ID}`

/**
 * Friendly-host authority for USER-FACING endpoints only (authorize, logout).
 * Both hosts serve the same endpoints, but this one keeps the tenant GUID out
 * of the user's address bar. Its discovery document still declares the
 * tenant-id issuer (verified 2026-07-18), so issuer/discovery/token stay on
 * AUTH_AUTHORITY — only browser-visible URLs may use this host. Sign-in and
 * sign-out must use the SAME host: the CIAM web session cookie is scoped to
 * the host the user authenticated on.
 */
export const AUTH_FRIENDLY_AUTHORITY = `https://${AUTH_TENANT_SUBDOMAIN}.ciamlogin.com/${AUTH_TENANT_ID}`

/** Authorization endpoint on the friendly host (the page users actually see). */
export const AUTH_AUTHORIZATION_ENDPOINT = `${AUTH_FRIENDLY_AUTHORITY}/oauth2/v2.0/authorize`

/** OIDC issuer for discovery (`${issuer}/.well-known/openid-configuration`). */
export const AUTH_ISSUER = `${AUTH_AUTHORITY}/v2.0`

/** Token endpoint used for refresh-token rotation (matches discovery). */
export const AUTH_TOKEN_ENDPOINT = `${AUTH_AUTHORITY}/oauth2/v2.0/token`

/**
 * Scopes for the CIAM single-tier setup. The bearer sent to the .NET API is
 * still the ID token (audience = clientId) — `access_as_user` authorizes
 * nothing here. It is requested to set the token request's **resource**, and
 * the resource decides which policy governs the refresh token's lifetime.
 *
 * With OIDC-only scopes, Entra resolves the resource to Microsoft Graph, so the
 * CA policy "Extended Login AgenticPlanner" (sign-in frequency 90 days) never
 * evaluates — sign-in logs showed `resourceDisplayName: Microsoft Graph` and an
 * empty `appliedConditionalAccessPolicies` on every Stana sign-in, even after
 * the policy was retargeted to All apps. External ID's default then capped
 * refresh-token inactivity at 24h (AADSTS700082, "inactive for 1.00:00:00").
 *
 * Naming our own API as the resource makes the 90-day policy apply. The scope is
 * already exposed and enabled on the app registration and admin-consented for
 * all principals, so this adds no consent prompt. Mobile mirrors this in
 * `src/providers/auth-provider.tsx` — keep them in lockstep.
 */
export const AUTH_SCOPES = [
  "openid",
  "profile",
  "email",
  "offline_access",
  `api://${AUTH_CLIENT_ID}/access_as_user`,
]

/** Auth.js provider id — determines the callback path (`/auth/callback/entra`). */
export const AUTH_PROVIDER_ID = "entra"

/**
 * Auth.js base path. Deliberately NOT under /api: next.config.ts rewrites
 * /api/* to the .NET backend, and a catch-all under /api/auth/* would shadow
 * the backend's /api/auth/config and /api/auth/link-request routes.
 */
export const AUTH_BASE_PATH = "/auth"

/** Session cookie lifetime — matches the ~90-day CIAM refresh-token inactivity window. */
export const AUTH_SESSION_MAX_AGE_SECONDS = 90 * 24 * 60 * 60

/**
 * Federated sign-out: ends the CIAM session too (parity with MSAL's
 * logoutRedirect), so a shared computer can't silently sign back in.
 * The post-logout redirect URI must be registered on the app registration.
 * Friendly host on purpose — sign-in happens there, and the CIAM session
 * cookie it must clear is scoped to that host.
 *
 * `idToken` becomes `id_token_hint`: without it Entra can't tell which session
 * to end, shows a "Pick an account" prompt (an empty dead-end when the CIAM
 * web session has already expired), and refuses to honor
 * post_logout_redirect_uri. With it, sign-out is silent and redirects back.
 * Callers must capture the token BEFORE tearing the BFF session down.
 */
export function federatedSignOutUrl(
  postLogoutRedirectUri: string,
  idToken?: string | null
): string {
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri,
  })
  if (idToken) params.set("id_token_hint", idToken)
  return `${AUTH_FRIENDLY_AUTHORITY}/oauth2/v2.0/logout?${params.toString()}`
}
